import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  Filter,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  Calendar,
  MoreHorizontal,
  Copy,
  Send,
  CheckCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { QuoteModal } from '@/components/travel/quotes/quote-modal';
import { ConvertToBookingModal } from '@/components/travel/quotes/convert-to-booking-modal';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useSessionState } from '@/hooks/use-session-state';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-purple-100 text-purple-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  DECLINED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-orange-100 text-orange-700',
  CONVERTED: 'bg-cyan-100 text-cyan-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'status.draft',
  SENT: 'status.sent',
  VIEWED: 'status.viewed',
  ACCEPTED: 'status.accepted',
  DECLINED: 'status.declined',
  EXPIRED: 'status.expired',
  CONVERTED: 'status.converted',
};

type QuoteStatus = 'DRAFT' | 'SENT' | 'VIEWED' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CONVERTED';

export function QuotesPage() {
  const { t } = useTranslation('quotes');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('QUOTES', 'CREATE');
  const canEdit = usePermission('QUOTES', 'EDIT');
  // Delete available for future actions
  const _canDelete = usePermission('QUOTES', 'DELETE');
  void _canDelete;
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'travel:quotes:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<QuoteStatus | undefined>(
    'travel:quotes:statusFilter',
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [selectedQuoteForConvert, setSelectedQuoteForConvert] = useState<{
    id: string;
    quoteNumber: string;
    totalAmount: number;
    currency: string;
    travelStartDate: string | Date | null;
    travelEndDate: string | Date | null;
    customer?: { firstName: string; lastName: string; email: string } | null;
    _count?: { items: number };
  } | null>(null);
  const limit = 10;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.quote.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter,
  });

  const { data: stats } = trpc.quote.stats.useQuery();

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.quote.updateStatus.useMutation({
    onSuccess: () => {
      utils.quote.list.invalidate();
      utils.quote.stats.invalidate();
    },
  });

  const duplicateMutation = trpc.quote.duplicate.useMutation({
    onSuccess: () => {
      utils.quote.list.invalidate();
      utils.quote.stats.invalidate();
    },
  });

  const quotes = data?.quotes ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const handleStatusChange = (quoteId: string, newStatus: QuoteStatus) => {
    updateStatusMutation.mutate({ id: quoteId, status: newStatus });
  };

  const handleDuplicate = (quoteId: string) => {
    duplicateMutation.mutate(quoteId);
  };

  const handleQuoteCreated = (quoteId: string) => {
    window.location.href = `/quotes/${quoteId}`;
  };

  const handleConvertToBooking = (quote: (typeof quotes)[0]) => {
    setSelectedQuoteForConvert({
      id: quote.id,
      quoteNumber: quote.quoteNumber,
      totalAmount: Number(quote.totalAmount),
      currency: quote.currency,
      travelStartDate: quote.travelStartDate,
      travelEndDate: quote.travelEndDate,
      customer: {
        firstName: quote.customer.firstName,
        lastName: quote.customer.lastName,
        email: quote.customer.email ?? '',
      },
      _count: quote._count,
    });
    setIsConvertModalOpen(true);
  };

  const handleConvertSuccess = (bookingId: string) => {
    utils.quote.list.invalidate();
    utils.quote.stats.invalidate();
    window.location.href = `/bookings/${bookingId}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createQuote')}
          </Button>
        )}
      </div>

      {/* Quote Modal */}
      {isModalOpen && (
        <QuoteModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          onSuccess={handleQuoteCreated}
        />
      )}

      {/* Convert to Booking Modal */}
      {selectedQuoteForConvert && (
        <ConvertToBookingModal
          open={isConvertModalOpen}
          onOpenChange={(open) => {
            setIsConvertModalOpen(open);
            if (!open) setSelectedQuoteForConvert(null);
          }}
          quote={selectedQuoteForConvert}
          onSuccess={handleConvertSuccess}
        />
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <div className="border-border bg-card border p-4">
            <p className="text-2xl font-bold">{stats.draft}</p>
            <p className="text-muted-foreground text-sm">{t('status.draft')}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-2xl font-bold">{stats.sent}</p>
            <p className="text-muted-foreground text-sm">{t('status.sent')}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-2xl font-bold">{stats.accepted}</p>
            <p className="text-muted-foreground text-sm">{t('status.accepted')}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-2xl font-bold">{stats.converted}</p>
            <p className="text-muted-foreground text-sm">{t('status.converted')}</p>
          </div>
          <div className="border-border bg-card border p-4">
            <p className="text-2xl font-bold text-green-600">{stats.conversionRate}%</p>
            <p className="text-muted-foreground text-sm">{t('conversionRate')}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as QuoteStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="DRAFT">{t('status.draft')}</option>
          <option value="SENT">{t('status.sent')}</option>
          <option value="VIEWED">{t('status.viewed')}</option>
          <option value="ACCEPTED">{t('status.accepted')}</option>
          <option value="DECLINED">{t('status.declined')}</option>
          <option value="EXPIRED">{t('status.expired')}</option>
          <option value="CONVERTED">{t('status.converted')}</option>
        </select>
        <Button variant="outline">
          <Filter className="mr-2 h-4 w-4" />
          {t('moreFilters')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loadingQuotes')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('errorLoading')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && quotes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('noQuotesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {debouncedSearch || statusFilter ? t('adjustFilters') : t('getStarted')}
          </p>
          {!debouncedSearch && !statusFilter && canCreate && (
            <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createQuote')}
            </Button>
          )}
        </div>
      )}

      {/* Quotes Table */}
      {!isLoading && !error && quotes.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('quoteNumber')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('customer')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('travelDates')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('validUntil')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{tCommon('status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('amount')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {quotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/travel/quotes/${quote.id}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {quote.quoteNumber}
                      </Link>
                      <p className="text-muted-foreground text-xs">
                        v{quote.version} • {quote._count.items} {t('items')}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                          <User className="text-primary h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {quote.customer?.firstName} {quote.customer?.lastName}
                          </p>
                          <p className="text-muted-foreground text-xs">{quote.customer?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {quote.travelStartDate && quote.travelEndDate ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="text-muted-foreground h-4 w-4" />
                          <span>
                            {new Date(quote.travelStartDate).toLocaleDateString()} -{' '}
                            {new Date(quote.travelEndDate).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">{t('notSet')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {new Date(quote.validUntil).toLocaleDateString()}
                      </span>
                      {new Date(quote.validUntil) < new Date() && quote.status !== 'CONVERTED' && (
                        <p className="text-xs text-red-500">{t('expired')}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium ${
                          statusColors[quote.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(statusLabelKeys[quote.status] || 'status.unknown') || quote.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">{formatCurrency(quote.totalAmount)}</p>
                      {quote.discountAmount > 0 && (
                        <p className="text-xs text-green-600">
                          -{formatCurrency(quote.discountAmount)} {t('discount')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/travel/quotes/${quote.id}`}>
                              <FileText className="mr-2 h-4 w-4" />
                              {t('viewDetails')}
                            </Link>
                          </DropdownMenuItem>
                          {canCreate && (
                            <DropdownMenuItem onClick={() => handleDuplicate(quote.id)}>
                              <Copy className="mr-2 h-4 w-4" />
                              {t('duplicate')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {canEdit && quote.status === 'DRAFT' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(quote.id, 'SENT')}>
                              <Send className="mr-2 h-4 w-4" />
                              {t('markAsSent')}
                            </DropdownMenuItem>
                          )}
                          {canEdit && quote.status === 'SENT' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, 'VIEWED')}
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                {t('markAsViewed')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, 'ACCEPTED')}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t('markAsAccepted')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, 'DECLINED')}
                              >
                                <XCircle className="mr-2 h-4 w-4" />
                                {t('markAsDeclined')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {canEdit && quote.status === 'VIEWED' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, 'ACCEPTED')}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t('markAsAccepted')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleStatusChange(quote.id, 'DECLINED')}
                              >
                                <XCircle className="mr-2 h-4 w-4" />
                                {t('markAsDeclined')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {canEdit && quote.status === 'ACCEPTED' && (
                            <DropdownMenuItem onClick={() => handleConvertToBooking(quote)}>
                              <ArrowRight className="mr-2 h-4 w-4" />
                              {t('convertToBooking')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {t('showingResults', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  {tCommon('next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
